const asObject = value => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const asRecords = value => Array.isArray(value) ? value.filter(item => item && typeof item === 'object' && !Array.isArray(item)) : [];

export const normalizePolicyReview = response => {
  const data = asObject(response);
  const defender = asObject(data.Defender);
  const oneDrive = asObject(data.OneDrive);
  const mailboxes = asObject(data.SharedMailboxReview);
  const rawSyncHealth = oneDrive.SyncHealth ?? data.SyncHealth;
  const syncHealth = asObject(rawSyncHealth);
  const unexpected = (section, value, known) => Object.entries(value)
    .filter(([key]) => !known.includes(key))
    .map(([key, field]) => ({ section, name: key, value: field }));
  const additional = [
    ...unexpected('Run', data, ['Status', 'SdpRequestId', 'GeneratedAtUtc', 'Defender', 'OneDrive', 'SharedMailboxReview', 'SyncHealth']),
    ...unexpected('Defender', defender, ['Summary', 'Vulnerabilities', 'RemediationTasks', 'ExportCsv', 'Errors']),
    ...unexpected('OneDrive', oneDrive, ['ReportPeriod', 'Summary', 'Accounts', 'SyncHealth']),
    ...unexpected('OneDrive Sync Health', syncHealth, ['Status', 'Source', 'ExportedAtUtc', 'ErrorDetailsScope', 'Summary', 'Devices', 'Warnings', 'Errors']),
    ...unexpected('Exchange (Shared Mailboxes)', mailboxes, ['ReviewCadenceDays', 'CreationAgeMinimumDays', 'Summary', 'Mailboxes', 'ReviewLedger'])
  ];
  if (mailboxes.ReviewLedger != null) additional.push({ section: 'Exchange (Shared Mailboxes)', name: 'ReviewLedger', value: mailboxes.ReviewLedger });

  return {
    status: data.Status || '',
    requestId: data.SdpRequestId || '',
    generatedAt: data.GeneratedAtUtc || '',
    additional,
    defender: {
      available: Boolean(data.Defender),
      summary: asObject(defender.Summary),
      rows: asRecords(defender.Vulnerabilities),
      remediation: asRecords(defender.RemediationTasks),
      errors: Array.isArray(defender.Errors) ? defender.Errors : []
    },
    oneDrive: {
      available: Boolean(data.OneDrive),
      period: oneDrive.ReportPeriod || '',
      summary: asObject(oneDrive.Summary),
      rows: asRecords(oneDrive.Accounts),
      syncHealth: {
        available: Boolean(rawSyncHealth && typeof rawSyncHealth === 'object'),
        status: syncHealth.Status || '',
        source: syncHealth.Source || '',
        exportedAt: syncHealth.ExportedAtUtc || '',
        errorScope: syncHealth.ErrorDetailsScope || '',
        summary: asObject(syncHealth.Summary),
        rows: asRecords(syncHealth.Devices),
        warnings: Array.isArray(syncHealth.Warnings) ? syncHealth.Warnings : [],
        errors: Array.isArray(syncHealth.Errors) ? syncHealth.Errors : []
      }
    },
    exchange: {
      available: Boolean(data.SharedMailboxReview),
      summary: asObject(mailboxes.Summary),
      cadenceDays: mailboxes.ReviewCadenceDays ?? null,
      minimumAgeDays: mailboxes.CreationAgeMinimumDays ?? null,
      rows: asRecords(mailboxes.Mailboxes),
      reviewLedger: mailboxes.ReviewLedger ?? null
    }
  };
};

export const POLICY_WORKLOADS = [
  {
    key: 'defender', label: 'Defender', sheet: 'Defender', singular: 'vulnerability',
    columns: [
      { key: 'Name', label: 'CVE' }, { key: 'Severity', label: 'Severity' },
      { key: 'CVSS', label: 'CVSS' }, { key: 'AffectedSoftware', label: 'Affected software' },
      { key: 'Age', label: 'Age' }, { key: 'AgeDays', label: 'Age (days)' },
      { key: 'PublishedOn', label: 'Published on', type: 'date' },
      { key: 'FirstDetected', label: 'First detected', type: 'date' },
      { key: 'UpdatedOn', label: 'Updated on', type: 'date' },
      { key: 'Threats', label: 'Threats', value: row => [
        ...(row.Threats?.ExploitTypes || []), ...(row.Threats?.ThreatNames || [])
      ].join(', ') },
      { key: 'ExposedDevices', label: 'Exposed devices' },
      { key: 'WorkflowCategory', label: 'Workflow' }
    ]
  },
  {
    key: 'oneDrive', label: 'OneDrive', sheet: 'OneDrive', singular: 'account',
    columns: [
      { key: 'UserDisplayName', label: 'Account' }, { key: 'UserPrincipalName', label: 'User principal name' },
      { key: 'LastActivityDate', label: 'Last activity', type: 'date' },
      { key: 'FileCount', label: 'Files' }, { key: 'ActiveFileCount', label: 'Active files' },
      { key: 'StorageUsedGB', label: 'Used (GB)' },
      { key: 'StorageAllocatedGB', label: 'Allocated (GB)' },
      { key: 'StoragePercentUsed', label: 'Used (%)' },
      { key: 'IsDeleted', label: 'Deleted', type: 'boolean' },
      { key: 'DeletedDate', label: 'Deleted on', type: 'date' },
      { key: 'ReportRefreshDate', label: 'Report refreshed', type: 'date' }
    ]
  },
  {
    key: 'exchange', label: 'Exchange (Shared Mailboxes)', sheet: 'Exchange (Shared Mailboxes)', singular: 'mailbox',
    columns: [
      { key: 'MailboxName', label: 'Mailbox' }, { key: 'PrimarySmtpAddress', label: 'Email' },
      { key: 'ReviewStatus', label: 'Review status' },
      { key: 'ReviewDue', label: 'Review due', type: 'boolean' },
      { key: 'CreatedAtUtc', label: 'Created', type: 'date' },
      { key: 'LastReviewedAtUtc', label: 'Last reviewed', type: 'date' },
      { key: 'AttestationWindowDays', label: 'Window (days)' }
    ]
  }
];

export const REMEDIATION_COLUMNS = [
  { key: 'title', label: 'Task' }, { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' }, { key: 'targetDevices', label: 'Target devices' },
  { key: 'fixedDevices', label: 'Fixed devices' },
  { key: 'createdOn', label: 'Created', type: 'date' },
  { key: 'dueOn', label: 'Due', type: 'date' },
  { key: 'requesterEmail', label: 'Requester' }
];

// Provisional: no device records seen yet; unmatched fields still appear in row details.
export const SYNC_HEALTH_COLUMNS = [
  { key: 'DeviceName', label: 'Device' }, { key: 'UserPrincipalName', label: 'User principal name' },
  { key: 'OperatingSystem', label: 'Operating system' }, { key: 'OneDriveVersion', label: 'OneDrive version' },
  { key: 'Status', label: 'Status' }, { key: 'ErrorCount', label: 'Errors' },
  { key: 'LastReportedAtUtc', label: 'Last reported', type: 'date' }
];

export const SYNC_HEALTH_OK_STATUSES = ['success', 'ok', 'completed'];

export const policyValue = (row, column) => column.value ? column.value(row) : row?.[column.key];

export const displayPolicyValue = (value, type) => {
  if (value == null || value === '') return '—';
  if (type === 'boolean') return value === true || String(value).toLowerCase() === 'true' ? 'Yes' : 'No';
  if (type === 'date') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? value : parsed.toLocaleString();
  }
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const extraPolicyFields = (row, columns) => {
  const known = new Set(columns.map(column => column.key));
  return Object.fromEntries(Object.entries(row).filter(([key]) => !known.has(key)));
};

const safeExcelValue = (value, type) => {
  if (value == null || value === '') return null;
  if (type === 'boolean') {
    if (typeof value === 'boolean') return value;
    if (/^(true|false)$/i.test(String(value))) return String(value).toLowerCase() === 'true';
  }
  if (type === 'date' && /^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(String(value))) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  const clean = Array.from(text).filter(char => /[\t\n\r\u0020-\ufffd]/u.test(char)).join('').slice(0, 32767);
  return /^[\s]*[=+@-]/.test(clean) ? `'${clean}` : clean;
};

const addSheet = (workbook, name, columns, records) => {
  const sheet = workbook.addWorksheet(name);
  sheet.columns = columns.map(column => ({
    header: column.label, key: column.key,
    width: Math.min(Math.max(column.label.length + 5, 17), 42),
    ...(column.type === 'date' ? { style: { numFmt: 'yyyy-mm-dd hh:mm' } } : {})
  }));
  sheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
  sheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF244A64' } };
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: Math.max(2, records.length + 1), column: columns.length } };
  records.forEach(record => {
    const values = columns.map(column => safeExcelValue(policyValue(record, column), column.type));
    sheet.addRow(values).alignment = { vertical: 'top', wrapText: true };
  });
  return sheet;
};

export const buildPolicyReviewWorkbook = async (data, ExcelJS) => {
  const workbook = new ExcelJS.Workbook();
  const summary = [];
  const addSummary = (section, values) => Object.entries(values).forEach(([name, value]) => {
    if (value == null || typeof value === 'object') return;
    summary.push({ section, name, value });
  });
  addSummary('Run', { Status: data.status, RequestId: data.requestId, GeneratedAtUtc: data.generatedAt });
  POLICY_WORKLOADS.forEach(workload => {
    const section = data[workload.key];
    addSummary(workload.label, { Available: section.available, ExportedRows: section.rows.length, ...section.summary });
  });
  addSummary('OneDrive', { ReportPeriod: data.oneDrive.period });
  const sync = data.oneDrive.syncHealth;
  addSummary('OneDrive Sync Health', {
    Available: sync.available, Status: sync.status, Source: sync.source, ExportedAtUtc: sync.exportedAt,
    ErrorDetailsScope: sync.errorScope, ExportedRows: sync.rows.length, ...sync.summary
  });
  addSummary('Exchange (Shared Mailboxes)', {
    ReviewCadenceDays: data.exchange.cadenceDays, CreationAgeMinimumDays: data.exchange.minimumAgeDays
  });
  addSummary('Defender', { RemediationTasks: data.defender.remediation.length, ErrorCount: data.defender.errors.length });
  addSheet(workbook, 'Summary', [
    { key: 'section', label: 'Section' }, { key: 'name', label: 'Metric' }, { key: 'value', label: 'Value' }
  ], summary);
  POLICY_WORKLOADS.forEach(workload => {
    const columns = [...workload.columns, { key: 'extra', label: 'Additional details', value: row => {
      const extra = extraPolicyFields(row, workload.columns);
      return Object.keys(extra).length ? extra : null;
    } }];
    addSheet(workbook, workload.sheet, columns, data[workload.key].rows);
  });
  addSheet(workbook, 'Defender Remediation', [
    ...REMEDIATION_COLUMNS,
    { key: 'extra', label: 'Additional details', value: row => extraPolicyFields(row, REMEDIATION_COLUMNS) }
  ], data.defender.remediation);
  addSheet(workbook, 'OneDrive Sync Health', [
    ...SYNC_HEALTH_COLUMNS,
    { key: 'extra', label: 'Additional details', value: row => {
      const extra = extraPolicyFields(row, SYNC_HEALTH_COLUMNS);
      return Object.keys(extra).length ? extra : null;
    } }
  ], sync.rows);
  const collectionErrors = [
    ...data.defender.errors.map(error => ({ section: 'Defender', type: 'Error', error })),
    ...sync.errors.map(error => ({ section: 'OneDrive Sync Health', type: 'Error', error })),
    ...sync.warnings.map(error => ({ section: 'OneDrive Sync Health', type: 'Warning', error }))
  ];
  if (collectionErrors.length) {
    addSheet(workbook, 'Collection Errors', [
      { key: 'error', label: 'Error' }, { key: 'section', label: 'Section' }, { key: 'type', label: 'Type' }
    ], collectionErrors);
  }
  if (data.additional.length) {
    addSheet(workbook, 'Additional data', [
      { key: 'section', label: 'Section' }, { key: 'name', label: 'Field' },
      { key: 'value', label: 'Value', value: row => typeof row.value === 'object' ? JSON.stringify(row.value)?.slice(0, 32000) : row.value }
    ], data.additional);
  }
  return workbook;
};

export const downloadPolicyReviewExcel = async (data, fileDate = new Date()) => {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = await buildPolicyReviewWorkbook(data, ExcelJS);
  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `sdp-policy-review-${new Date(fileDate).toISOString().slice(0, 10)}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
};